import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Alert, Button } from '@opendate-app/setlist';

const Form = styled.div`
  display: grid;
  gap: 16px;
`;

export const isSetupIntentSecret = (clientSecret) => String(clientSecret || '').startsWith('seti_');

const PayForm = ({ clientSecret, label, returnUrl, onPaid, onLockChange, elementOptions }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [paid, setPaid] = useState(false);

  useEffect(() => {
    if (onLockChange) onLockChange(busy || paid);
  }, [busy, paid]);

  const confirmWithStripe = async () => {
    const confirmMethod = isSetupIntentSecret(clientSecret) ? 'confirmSetup' : 'confirmPayment';
    const { error: stripeError } = await stripe[confirmMethod]({
      elements,
      confirmParams: { return_url: returnUrl },
      redirect: 'if_required',
    });
    if (stripeError) throw stripeError;
    setPaid(true);
  };

  const pay = async () => {
    setBusy(true);
    setError(null);
    try {
      if (!paid) await confirmWithStripe();
    } catch (stripeError) {
      setError(stripeError.message);
      setBusy(false);
      return;
    }
    try {
      await onPaid();
    } catch (paidError) {
      setError(paidError.message);
      setBusy(false);
    }
  };

  return (
    <Form>
      <PaymentElement options={elementOptions} />
      {error && <Alert color="error" title={error} />}
      <Button variant="primary" onClick={pay} disabled={!stripe || !elements || busy}>
        {busy ? 'Processing…' : paid ? 'Finish your order' : label}
      </Button>
    </Form>
  );
};

PayForm.propTypes = {
  clientSecret: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  returnUrl: PropTypes.string.isRequired,
  onPaid: PropTypes.func.isRequired,
  onLockChange: PropTypes.func,
  elementOptions: PropTypes.object,
};

PayForm.defaultProps = {
  onLockChange: null,
  elementOptions: undefined,
};

export default PayForm;
